// Страница-оркестратор прохождения упражнения: управляет фазами (загрузка, выбор варианта, выполнение).
// Маршрутизирует к нужному экрану в зависимости от типа упражнения (keyword_transform, lexics, text_fill_bank).

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, Spinner, Alert, Button, Modal } from 'react-bootstrap';
import AppNavbar from '../../components/common/AppNavbar';
import useAuthStore from '../../store/authStore';
import useGuestStore from '../../store/guestStore';
import StudentVariantSelectScreen from '../../components/students/exercises/StudentVariantSelectScreen';
import StudentTextFillBankScreen from '../../components/students/exercises/StudentTextFillBankScreen';
import StudentExercise from '../../components/students/exercises/StudentExercise';
import {
  getStudentExercise,
  getStudentVariant,
  getStudentHistory,
  getVariantFullText,
  startVariant,
} from '../../api/studentApi';

export default function StudentExercisePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { exerciseId: id, variantId: variantIdParam } = location.state ?? {};

  const token = useAuthStore((s) => s.token);
  const isGuest = !token;
  const guestStore = useGuestStore();
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Если попали сюда без state (например, прямой ввод URL) — редирект в каталог
  useEffect(() => {
    if (!id) navigate('/student/exercises', { replace: true });
  }, [id, navigate]);

  // Этапы: 'loading' | 'error' | 'select_variant' | 'exercise'
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');

  const [exercise, setExercise] = useState(null);
  const [variants, setVariants] = useState([]);

  const [selectedVariantId, setSelectedVariantId] = useState(variantIdParam ? Number(variantIdParam) : null);
  const [variantData, setVariantData] = useState(null);
  const [historyId, setHistoryId] = useState(null);

  const [variantHistoryMap, setVariantHistoryMap] = useState({});
  const [preSelectedVariantId, setPreSelectedVariantId] = useState(null);

  const [showFullTextModal, setShowFullTextModal] = useState(false);
  const [fullText, setFullText] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setPhase('loading');

        const { data: exDetail } = await getStudentExercise(id);
        if (cancelled) return;

        setExercise(exDetail);
        const exVariants = exDetail.exercise_variants ?? [];

        if (variantIdParam) {
          setSelectedVariantId(Number(variantIdParam));
          const [{ data: vData }, { data: startData }] = await Promise.all([
            getStudentVariant(variantIdParam),
            startVariant(variantIdParam),
          ]);
          if (cancelled) return;
          setVariantData(vData);
          setHistoryId(startData.history_id);
          if ((vData?.form_type ?? 'keyword_transform') === 'keyword_transform') {
            try {
              const { data: ftData } = await getVariantFullText(variantIdParam);
              if (!cancelled) setFullText(ftData.text ?? '');
            } catch {
              if (!cancelled) setFullText('');
            }
            if (!cancelled) setShowFullTextModal(true);
          }
          setPhase('exercise');
          return;
        }

        if (exVariants.length === 0) {
          setError('У этого упражнения пока нет вариантов');
          setPhase('error');
          return;
        }

        let history = [];
        try {
          const { data: historyData } = await getStudentHistory(100, 0);
          if (!cancelled) history = historyData ?? [];
        } catch {
          // История недоступна — продолжаем без неё
        }
        if (cancelled) return;

        const exerciseVariantIds = new Set(exVariants.map((v) => v.id));
        const map = {};
        for (const h of history) {
          if (h.variant_id && exerciseVariantIds.has(h.variant_id) && !map[h.variant_id]) {
            map[h.variant_id] = { status: h.status, score_percent: h.score_percent ?? null };
          }
        }
        setVariantHistoryMap(map);

        const completedIndices = exVariants
          .map((v, idx) => ({ v, idx }))
          .filter(({ v }) => map[v.id]?.status === 1)
          .map(({ idx }) => idx);

        let preSelected;
        if (completedIndices.length === 0) {
          preSelected = exVariants[0].id;
        } else {
          const lastCompletedIdx = Math.max(...completedIndices);
          const nextIdx = lastCompletedIdx + 1 < exVariants.length ? lastCompletedIdx + 1 : 0;
          preSelected = exVariants[nextIdx].id;
        }
        setPreSelectedVariantId(preSelected);
        setVariants(exVariants);
        setPhase('select_variant');
      } catch {
        if (!cancelled) {
          setError('Не удалось загрузить упражнение');
          setPhase('error');
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [id, variantIdParam, navigate]);

  const handleSelectVariant = async (vid) => {
    if (isGuest && guestStore.isLimitReached()) {
      setShowLimitModal(true);
      return;
    }
    setPhase('loading');
    try {
      const [{ data: vData }, { data: startData }] = await Promise.all([
        getStudentVariant(vid),
        startVariant(vid),
      ]);
      setSelectedVariantId(vid);
      setVariantData(vData);
      setHistoryId(startData.history_id);
      if ((vData?.form_type ?? 'keyword_transform') === 'keyword_transform') {
        try {
          const { data: ftData } = await getVariantFullText(vid);
          setFullText(ftData.text ?? '');
        } catch {
          setFullText('');
        }
        setShowFullTextModal(true);
      }
      setPhase('exercise');
    } catch {
      setError('Не удалось загрузить вариант');
      setPhase('error');
    }
  };

  if (phase === 'loading') return (
    <><AppNavbar /><div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 56px)' }}><div className="text-center"><Spinner animation="border" variant="primary" /><p className="text-muted mt-3 mb-0" style={{ fontSize: '0.9rem' }}>Загружаем упражнение...</p></div></div></>
  );

  if (phase === 'error') return (
    <><AppNavbar /><Container className="py-4"><Alert variant="danger" style={{ borderRadius: '0.75rem' }}>{error}</Alert><Button variant="outline-secondary" onClick={() => navigate('/student/exercises')} style={{ borderRadius: '0.5rem' }}>← Вернуться к упражнениям</Button></Container></>
  );

  if (phase === 'select_variant') {
    return (
      <StudentVariantSelectScreen
        exercise={exercise}
        variants={variants}
        onSelect={handleSelectVariant}
        onBack={() => navigate('/student/exercises')}
        variantHistoryMap={variantHistoryMap}
        preSelectedVariantId={preSelectedVariantId}
      />
    );
  }

  if (phase === 'exercise') {
    const formType = variantData?.form_type ?? 'keyword_transform';
    const exerciseScreen = formType === 'text_fill_bank' ? (
      <StudentTextFillBankScreen
        variant={variantData}
        historyId={historyId}
        exerciseId={id}
        variantId={selectedVariantId}
        navigate={navigate}
        onInterrupt={() => navigate('/student/exercises')}
      />
    ) : (
      <StudentExercise
        variant={variantData}
        historyId={historyId}
        exerciseId={id}
        variantId={selectedVariantId}
        navigate={navigate}
        onInterrupt={() => navigate('/student/exercises')}
      />
    );

    return (
      <>
        {exerciseScreen}
        <Modal
          show={showFullTextModal}
          onHide={() => setShowFullTextModal(false)}
          centered
          size="lg"
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', padding: '1.25rem 1.5rem' }}>
            <Modal.Title style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1e2a4a' }}>
              📄 Прочитайте текст
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '1.5rem', maxHeight: '60vh', overflowY: 'auto' }}>
            <p style={{ lineHeight: 1.8, color: '#1e2a4a', whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.95rem' }}>
              {fullText}
            </p>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '1rem 1.5rem' }}>
            <Button
              onClick={() => setShowFullTextModal(false)}
              style={{
                background: 'linear-gradient(135deg, #4f6ef7 0%, #7c3aed 100%)',
                border: 'none',
                borderRadius: '0.5rem',
                fontWeight: 600,
                padding: '0.5rem 1.5rem',
              }}
            >
              Далее →
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  // Модал лимита для гостей (показывается до начала упражнения)
  return (
    <>
      <AppNavbar />
      <Modal show={showLimitModal} onHide={() => setShowLimitModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700 }}>Лимит исчерпан</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🎯</div>
          <p className="fw-semibold mb-2">Вы прошли доступные бесплатные упражнения</p>
          <p className="text-muted" style={{ fontSize: '0.9rem' }}>
            Зарегистрируйтесь бесплатно, чтобы продолжить тренироваться и сохранять прогресс.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-2">
          <Button variant="primary" onClick={() => navigate('/register')} style={{ fontWeight: 600, borderRadius: '0.5rem' }}>
            Зарегистрироваться бесплатно
          </Button>
          <Button variant="outline-secondary" onClick={() => navigate('/login')} style={{ borderRadius: '0.5rem' }}>
            Войти
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
